<template>
  <div class="cdl-page">
    <b-card no-body class="cdl-page__card">
      <!-- Hero header -->
      <div class="cdl-page__hero">
        <div class="cdl-page__hero-icon"><i :class="segIcon"></i></div>
        <div class="cdl-page__hero-text">
          <h4 class="cdl-page__hero-title">{{ title }}</h4>
          <span class="cdl-page__hero-sub">
            <b-spinner small v-if="loading" class="mr-50"/>
            <template v-else><strong>{{ total }}</strong> {{ $t('clientDashboard.clientsIn') }} {{ subtitle }}</template>
          </span>
        </div>
      </div>

      <!-- Bulk actions over the selected clients (same as the report task) -->
      <client-bulk-actions v-if="selectedIds.length" class="cdl-page__bulk"
                           :selected-ids="selectedIds" :scope="scope" @applied="onBulkApplied"/>

      <b-overlay :show="loading" rounded opacity="0.4" spinner-variant="primary">
        <div class="cdl-page__table-wrap">
          <b-table responsive hover :items="items" :fields="fields" show-empty class="cdl-table mb-0"
                   @row-clicked="onRowClicked">
            <template #head(selection)>
              <b-form-checkbox :checked="allSelected" :indeterminate="someSelected" @change="toggleSelectAll"/>
            </template>
            <template #cell(selection)="data">
              <b-form-checkbox :checked="!!selected[data.item.id]" @change="val => toggleRow(data.item.id, val)"
                               @click.native.stop/>
            </template>
            <template #cell(name)="data">
              <div class="cdl-name">
                <span class="cdl-name__main">{{ fullName(data.item) }}</span>
                <b-badge v-if="data.item.vip" variant="light-warning" pill class="ml-50">VIP</b-badge>
              </div>
              <small class="text-muted" v-if="data.item.accountId">#{{ data.item.accountId }}</small>
            </template>
            <template #cell(email)="data"><span class="cdl-ellipsis">{{ data.value || '—' }}</span></template>
            <template #cell(country)="data"><span>{{ data.value || '—' }}</span></template>
            <template #cell(representativeUserId)="data">
              <span>{{ data.value ? getUserFullName(data.value) : '—' }}</span>
            </template>
            <template #cell(ftdTime)="data">
              <span v-if="data.value" class="text-success font-weight-bold">{{ data.value | momentDate }}</span>
              <span v-else class="text-muted">—</span>
            </template>
            <template #cell(stdTime)="data">
              <span v-if="data.value" class="text-warning font-weight-bold">{{ data.value | momentDate }}</span>
              <span v-else class="text-muted">—</span>
            </template>
            <template #cell(totalDeposits)="data">
              <span class="font-weight-bold" :class="(data.value || 0) > 0 ? 'text-success' : 'text-muted'">
                <template v-if="data.item.currency">{{ convertToCurrency(data.value || 0, data.item.currency) }}</template>
                <template v-else>{{ data.value || 0 }}</template>
              </span>
              <b-badge v-if="data.item.depositCount" pill variant="light-success" class="ml-25"
                       v-b-tooltip.hover :title="$t('salesDashboard.depositsCount')">
                {{ data.item.depositCount }}
              </b-badge>
            </template>
            <template #cell(depositorName)="data"><span>{{ data.value || '—' }}</span></template>
            <template #cell(lastNoteTime)="data">
              <div v-if="data.value">
                <span :class="noteAgeClass(data.value)">{{ data.value | momentDate }}</span>
                <small class="text-muted d-block cdl-ellipsis" v-if="data.item.lastNoteText"
                       v-b-tooltip.hover :title="data.item.lastNoteText">{{ data.item.lastNoteText }}</small>
              </div>
              <span v-else class="text-muted">—</span>
            </template>
            <template #cell(open)="data">
              <b-button size="sm" variant="flat-primary" class="btn-icon" @click.stop="openClient(data.item)"
                        v-b-tooltip.hover :title="$t('clientDashboard.openClient')">
                <i class="fa-solid fa-arrow-up-right-from-square"></i>
              </b-button>
            </template>
            <template #empty><div class="text-center text-muted py-3">{{ $t('clientDashboard.noClients') }}</div></template>
          </b-table>
        </div>

        <div class="cdl-page__footer" v-if="total > 0">
          <b-pagination v-model="page" :total-rows="total" :per-page="size" size="sm" class="mb-0" @change="onPageChange"/>
          <div class="cdl-page__per-page">
            <b-form-select v-model="size" size="sm" :options="[25, 50, 100, 250]" @change="onSizeChange" style="width: 90px"/>
            <span class="text-muted ml-50">/ {{ total }}</span>
          </div>
        </div>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import ClientBulkActions from '@/components/modal/ClientBulkActions';

const SEG_ICON = {
  total: 'fa-solid fa-users',
  age0to15: 'fa-solid fa-clock',
  age15to30: 'fa-solid fa-hourglass-half',
  age30plus: 'fa-solid fa-triangle-exclamation',
  ftd: 'fa-solid fa-right-to-bracket',
  std: 'fa-solid fa-repeat',
  neverStd: 'fa-solid fa-user-clock',
};

export default {
  name: 'ClientDashboardList',
  components: { ClientBulkActions },
  mixins: [mixinBase, mixinNotifications],
  data: () => ({
    loading: false,
    items: [],
    total: 0,
    page: 1,
    size: 50,
    segment: 'total',
    title: '',
    subtitle: '',
    selected: {},
    ctx: { orgIds: [], buIds: [], buTypes: [], range: { from: null, until: null } },
  }),
  computed: {
    ...mapGetters('data', ['getUserFullName']),
    segIcon() { return SEG_ICON[this.segment] || 'fa-solid fa-users'; },
    selectedIds() { return Object.keys(this.selected).filter(id => this.selected[id]); },
    allSelected() { return this.items.length > 0 && this.items.every(c => this.selected[c.id]); },
    someSelected() { return this.items.some(c => this.selected[c.id]) && !this.allSelected; },
    scope() { return { orgIds: this.ctx.orgIds || [], buIds: this.ctx.buIds || [] }; },
    fields() {
      return [
        { key: 'selection', label: '', class: 'cdl-sel-cell' },
        { key: 'name', label: this.$t('clientDashboard.client') },
        { key: 'email', label: this.$t('columns.email') },
        { key: 'country', label: this.$t('columns.country') },
        { key: 'representativeUserId', label: this.$t('columns.representative') },
        { key: 'ftdTime', label: this.$t('clientDashboard.ftd'), class: 'text-nowrap' },
        { key: 'stdTime', label: this.$t('clientDashboard.std'), class: 'text-nowrap' },
        { key: 'totalDeposits', label: this.$t('clientDashboard.totalDeposited'), class: 'text-right text-nowrap' },
        { key: 'depositorName', label: this.$t('columns.depositorUserId') },
        { key: 'lastNoteTime', label: this.$t('clientDashboard.lastNote') },
        { key: 'open', label: '', class: 'text-right' },
      ];
    },
  },
  created() {
    const q = this.$route.query || {};
    const split = v => (v ? String(v).split(',').filter(Boolean) : []);
    this.segment = q.segment || 'total';
    this.title = q.title || this.$t('clientDashboard.client');
    this.subtitle = this.segLabel(this.segment);
    this.ctx = {
      orgIds: split(q.orgIds),
      buIds: split(q.buIds),
      buTypes: split(q.buTypes),
      representativeUserIds: split(q.representativeUserIds),
      range: { from: q.from || null, until: q.until || null },
    };
    if (q.title) document.title = q.title;
    this.fetch();
  },
  methods: {
    ...mapActions('dashboard', ['getClientDashboardClients']),
    segLabel(seg) {
      const map = {
        total: 'clientDashboard.totalClients', age0to15: 'clientDashboard.age0to15',
        age15to30: 'clientDashboard.age15to30', age30plus: 'clientDashboard.age30plus',
        ftd: 'clientDashboard.ftd', std: 'clientDashboard.std', neverStd: 'clientDashboard.neverStd',
      };
      return map[seg] ? this.$t(map[seg]) : seg;
    },
    fetch() {
      this.loading = true;
      this.getClientDashboardClients({
        segment: this.segment,
        orgIds: this.ctx.orgIds,
        buIds: this.ctx.buIds,
        buTypes: this.ctx.buTypes,
        representativeUserIds: this.ctx.representativeUserIds,
        range: this.ctx.range,
        pagination: { page: this.page, size: this.size },
      })
        .then(
          res => {
            this.items = Array.isArray(res.data) ? res.data : [];
            this.total = res.metadata && res.metadata.total != null ? res.metadata.total : this.items.length;
          },
          err => this.showServerErrorNotification('fetchDataError', 'clientDashboard', err)
        )
        .finally(() => { this.loading = false; });
    },
    onPageChange(p) { this.page = p; this.selected = {}; this.$nextTick(this.fetch); },
    onSizeChange() { this.page = 1; this.selected = {}; this.$nextTick(this.fetch); },
    toggleRow(id, val) { this.$set(this.selected, id, !!val); },
    toggleSelectAll(val) {
      const next = {};
      if (val) this.items.forEach(c => { next[c.id] = true; });
      this.selected = next;
    },
    onBulkApplied() { this.selected = {}; this.fetch(); },
    fullName(item) { return [item.firstName, item.lastName].filter(Boolean).join(' ') || item.id; },
    noteAgeClass(time) {
      if (!time) return 'text-muted';
      const days = this.$moment().diff(this.$moment(time), 'days');
      if (days <= 15) return 'text-success';
      if (days <= 30) return 'text-warning';
      return 'text-danger';
    },
    onRowClicked(item) { this.openClient(item); },
    openClient(item) {
      // Open the client modal in this same page (not a new tab).
      if (!item || !item.id) return;
      this.showClient(item.id);
    },
  },
};
</script>

<style lang="scss">
.cdl-page {
  padding: 1.5rem;

  &__card { border-radius: 18px; overflow: hidden; box-shadow: 0 14px 40px -20px rgba(20, 24, 50, 0.35); }
  &__bulk { padding: 0.75rem 1.25rem 0; }

  &__hero {
    display: flex; align-items: center; gap: 1rem;
    padding: 1.25rem 1.5rem; color: #fff;
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%);
  }
  &__hero-icon {
    display: flex; align-items: center; justify-content: center;
    width: 48px; height: 48px; border-radius: 13px; font-size: 1.3rem;
    background: rgba(255, 255, 255, 0.22); flex-shrink: 0;
  }
  &__hero-text { flex: 1; min-width: 0; }
  &__hero-title { margin: 0; font-weight: 700; font-size: 1.25rem; }
  &__hero-sub { font-size: 0.85rem; opacity: 0.92; }

  &__table-wrap { max-height: calc(100vh - 230px); overflow-y: auto; }
  &__footer {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0.85rem 1.25rem; border-top: 1px solid rgba(110, 117, 159, 0.12);
  }
  &__per-page { display: flex; align-items: center; }
}

.cdl-table {
  font-size: 0.85rem;
  thead th { background: #f7f9fc; color: #6e6b7b; text-transform: uppercase; font-size: 0.7rem; letter-spacing: 0.04em; }
  tbody tr { cursor: pointer; }
  .cdl-name__main { font-weight: 600; }
  .cdl-ellipsis { display: inline-block; max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: bottom; }
}

body.dark-layout {
  .cdl-page__card { background: var(--crm-dark-surface, #232c3e); color: #d0d2d6; }
  .cdl-page__footer { border-top-color: rgba(255, 255, 255, 0.08); }
  .cdl-table thead th { background: var(--crm-dark-surface-2, #2e3850); color: #8b93ab; }
}
</style>
